import { Component, effect, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  DeviceUsageTypeV2,
  JobState,
  JobStatus,
  LineUsageTypeV2,
  MigrationStatusV2,
  NumberReportEntry,
  RingCentralFields,
} from '../../../shared/models/generated/ziroModels';
import { BottomNavService, BottomNavUpdateButtonsList } from '../../../shared/bottom-nav/bottom-nav.service';
import { ButtonStyles } from '../../../button/button.component';
import { combineLatest, Subscription } from 'rxjs';
import { ZiroIcons } from '../../../shared/models/ziro-icons.enum';
import { ChartData } from '../../migrations-cards/migrations-card-abstract.directive';
import { MigrationsService, UiDeviceType, UiLineType, UiMigrationStatus } from '../../services/migrations.service';
import { ToastService } from '../../../shared/services/toast.service';
import { ToastTypes } from '../../../shared/services/abstract/toast.service.abstract';
import { MigrationsAssessmentsContext } from '../../services/migrations-assessments.context';
import { MigrationsNumberContext, MigrationsNumberReport } from '../../services/migrations-numbers-context.service';
import { MigrationsDashboardService } from '../../services/migrations-dashboard.service';

export type RcWidgetFilter = 'lineType' | 'deviceType' | 'migrationStatus';

@Component({
  selector: 'ziro-migrations-ring-central-dashboard',
  templateUrl: 'migrations-ring-central-dashboard.component.html',
  styleUrls: ['../../migrations.component.scss'],
  providers: [MigrationsService, MigrationsDashboardService],
  standalone: false,
})
export class MigrationsRingCentralDashboardComponent implements OnInit, OnDestroy {
  private readonly _refreshButtonId = 'migrations-ring-central-dashboard-refresh';

  private _route = inject(ActivatedRoute);
  private _router = inject(Router);
  private _toastService = inject(ToastService);
  private _migrationsAssessmentsContext = inject(MigrationsAssessmentsContext);
  private _ringCentralNumberContext = inject(MigrationsNumberContext);
  private _dashboardService = inject(MigrationsDashboardService);
  private _bottomNavService = inject(BottomNavService);

  private _assessmentId: string;
  private _tenantId: string;
  private _assessmentName: string;
  private _jobState: JobState;
  private _subscription = new Subscription();
  private _reportDataSubscription: Subscription;

  colorScheme = this._dashboardService.createDefaultColorScheme();
  migrationStatusCustomColors = this._dashboardService.migrationStatusCustomColors;
  usageTypeCustomColors = this._dashboardService.usageTypeCustomColors;
  deviceTypeCustomColors = this._dashboardService.deviceTypeCustomColors;

  isLoading = true;
  isReportWidgetError = false;
  migrationStatusData: ChartData[] = [];
  usageTypeData: ChartData[] = [];
  deviceTypeData: ChartData[] = [];

  isReportDataRefreshing = signal<boolean>(true);

  constructor() {
    effect(() => {
      this._dashboardService.setRefreshButtonPendingState(this._refreshButtonId, this.isReportDataRefreshing());
    });
  }

  ngOnInit(): void {
    this._assessmentId = this._route.snapshot.paramMap.get('id');
    this._tenantId = this._route.snapshot.paramMap.get('tenantId');
    this._initBottomNav();
    this._initContexts();
  }

  ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }

  onNumbersReportFilter(filter: string, filterType: RcWidgetFilter): void {
    if (filter) {
      this._router.navigate(['numbers-report', `${filterType}=${encodeURIComponent(filter)}`], {
        relativeTo: this._route,
      });
    } else {
      this._router.navigate(['numbers-report'], { relativeTo: this._route });
    }
  }

  onRefreshClick(): void {
    this._refreshData();
  }

  private _initContexts(): void {
    const sub = combineLatest([
      this._migrationsAssessmentsContext.state$,
      this._ringCentralNumberContext.jobState$,
    ]).subscribe(([allExports, reportJobStatus]) => {
      const assessment = allExports.ringCentralExports.find((rc) => rc.exportId === this._assessmentId);
      this._assessmentName = assessment?.name ?? '';

      this._dashboardService.updateDashboardBreadcrumbs(this._tenantId, this._assessmentName);
      this.isLoading = false;

      this._handleContextPolling(reportJobStatus);

      if (reportJobStatus.jobState === JobState.IDLE && reportJobStatus.lastRunTime !== null) {
        if (reportJobStatus.error !== null) {
          this.isReportWidgetError = true;
          this.isReportDataRefreshing.set(false);
        } else {
          this.isReportWidgetError = false;
          this._getReportData();
        }
      }
    });
    this._subscription.add(sub);
  }

  private _handleContextPolling(reportJobStatus: JobStatus): void {
    this._jobState = reportJobStatus.jobState;
    const hasData = this.migrationStatusData.length > 0;
    this.isReportDataRefreshing.set(reportJobStatus.jobState !== JobState.IDLE || !hasData);
  }

  private _getReportData(): void {
    this._reportDataSubscription?.unsubscribe();
    this._reportDataSubscription = this._ringCentralNumberContext.state$.subscribe(
      (report: MigrationsNumberReport<RingCentralFields>) => {
        this._setChartData(report.entries);
        if (this._jobState === JobState.IDLE) {
          this.isReportDataRefreshing.set(false);
        }
      },
    );
    this._subscription.add(this._reportDataSubscription);
  }

  private _setChartData(entries: NumberReportEntry<RingCentralFields>[]): void {
    this.migrationStatusData = this._createMigrationStatusChartData(entries);
    this.usageTypeData = this._createUsageTypeChartData(entries);
    this.deviceTypeData = this._createDeviceTypeChartData(entries);
  }

  private _createMigrationStatusChartData(entries: NumberReportEntry<RingCentralFields>[]): ChartData[] {
    const statusCounts = new Map<string, number>();
    Object.values(MigrationStatusV2).forEach((status) => statusCounts.set(status, 0));
    entries.forEach((entry) =>
      statusCounts.set(entry.migrationStatus, (statusCounts.get(entry.migrationStatus) || 0) + 1),
    );

    const migrationStatusLabels = new Map<string, string>([
      [MigrationStatusV2.MIGRATED, UiMigrationStatus.MIGRATED],
      [MigrationStatusV2.IGNORED, UiMigrationStatus.IGNORED],
      [MigrationStatusV2.NOT_READY, UiMigrationStatus.NOT_READY],
      [MigrationStatusV2.READY, UiMigrationStatus.READY],
      [MigrationStatusV2.STAGED, UiMigrationStatus.STAGED],
    ]);

    return Array.from(statusCounts.entries())
      .map(([status, count]) => ({
        name: migrationStatusLabels.get(status),
        value: count,
        link: ['numbers-report', `migrationStatus=${encodeURIComponent(migrationStatusLabels.get(status))}`],
      }))
      .sort((a, b) => b.value - a.value);
  }

  private _createUsageTypeChartData(entries: NumberReportEntry<RingCentralFields>[]): ChartData[] {
    const lineTypeCounts = new Map<string, number>();
    Object.values(LineUsageTypeV2).forEach((t) => lineTypeCounts.set(t, 0));
    entries.forEach((e) =>
      lineTypeCounts.set(e.numberInsights.lineUsageType, (lineTypeCounts.get(e.numberInsights.lineUsageType) || 0) + 1),
    );

    const lineTypeLabels = new Map<string, string>([
      [LineUsageTypeV2.USER, UiLineType.USER],
      [LineUsageTypeV2.CONTACT_CENTER_AGENT, UiLineType.CONTACT_CENTER_AGENT],
      [LineUsageTypeV2.SHARED_DEVICE, UiLineType.SHARED_DEVICE],
      [LineUsageTypeV2.SHARED_LINE, UiLineType.SHARED_LINE],
      [LineUsageTypeV2.MULTI_LINE_USER, UiLineType.MULTI_LINE_USER],
      [LineUsageTypeV2.VOICE_APP, UiLineType.VOICE_APP],
      [LineUsageTypeV2.ANALOG, UiLineType.ANALOG],
      [LineUsageTypeV2.INACTIVE, UiLineType.INACTIVE],
      [LineUsageTypeV2.AUTO_ATTENDANT, UiLineType.AUTO_ATTENDANT],
      [LineUsageTypeV2.ORPHAN, UiLineType.ORPHAN],
      [LineUsageTypeV2.UNCLASSIFIED, UiLineType.UNCLASSIFIED],
    ]);

    return Array.from(lineTypeCounts.entries())
      .map(([type, count]) => ({
        name: lineTypeLabels.get(type),
        value: count,
        link: ['numbers-report', `lineType=${encodeURIComponent(lineTypeLabels.get(type))}`],
      }))
      .sort((a, b) => b.value - a.value);
  }

  private _createDeviceTypeChartData(entries: NumberReportEntry<RingCentralFields>[]): ChartData[] {
    const deviceTypeCounts = new Map<string, number>();
    Object.values(DeviceUsageTypeV2).forEach((t) => deviceTypeCounts.set(t, 0));
    entries.forEach((e) =>
      deviceTypeCounts.set(
        e.numberInsights.deviceUsageType,
        (deviceTypeCounts.get(e.numberInsights.deviceUsageType) || 0) + 1,
      ),
    );

    const deviceTypeLabels = new Map<string, string>([
      [DeviceUsageTypeV2.SOFTPHONE_ONLY, UiDeviceType.SOFTPHONE_ONLY],
      [DeviceUsageTypeV2.DESKPHONE, UiDeviceType.DESKPHONE],
      [DeviceUsageTypeV2.HOT_DESKING, UiDeviceType.HOT_DESKING],
      [DeviceUsageTypeV2.CTI_PORT, UiDeviceType.CTI_PORT],
      [DeviceUsageTypeV2.ANALOG_FAX, UiDeviceType.ANALOG_FAX],
      [DeviceUsageTypeV2.ANALOG_MODEM, UiDeviceType.ANALOG_MODEM],
      [DeviceUsageTypeV2.ANALOG_OTHER, UiDeviceType.ANALOG_OTHER],
      [DeviceUsageTypeV2.MIXED, UiDeviceType.MIXED],
      [DeviceUsageTypeV2.NO_DEVICE, UiDeviceType.NO_DEVICE],
    ]);

    return Array.from(deviceTypeCounts.entries())
      .map(([type, count]) => ({
        name: deviceTypeLabels.get(type),
        value: count,
        link: ['numbers-report', `deviceType=${encodeURIComponent(deviceTypeLabels.get(type))}`],
      }))
      .sort((a, b) => b.value - a.value);
  }

  private _refreshData(): void {
    this.migrationStatusData = [];
    this.usageTypeData = [];
    this.deviceTypeData = [];

    this.isReportDataRefreshing.set(true);

    if (this._jobState === JobState.IDLE) {
      this._ringCentralNumberContext.refresh().subscribe({
        next: () => {
          this._toastService.push(
            ToastTypes.SUCCESS,
            ZiroIcons.REFRESH,
            'tkey;migrations.dashboard.refresh_toast.title',
            'tkey;migrations.dashboard.refresh_toast.message',
          );
        },
      });
    }
  }

  private _initBottomNav(): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonsList([
        {
          id: 'migrations-ring-central-dashboard-back',
          dataAutomation: 'migrations-ring-central-dashboard-back-button',
          label: 'tkey;global.button.back.text',
          buttonClass: ButtonStyles.DEFAULT,
          icon: ZiroIcons.BACK_CIRCLE,
          cb: () => this._router.navigate(['../../'], { relativeTo: this._route }),
        },
        {
          id: this._refreshButtonId,
          dataAutomation: 'migrations-ring-central-dashboard-refresh-button',
          label: 'tkey;migrations.dashboard.refresh_button.label',
          icon: ZiroIcons.REFRESH,
          buttonClass: ButtonStyles.PRIMARY,
          state: {
            pending: false,
            buttonDisableState: { disabled: false },
          },
          cb: () => this.onRefreshClick(),
        },
      ]),
    );
  }
}
